import { geoData } from '@fs/zion-config'
import zionDebug from '@fs/zion-debug'
import type { SyntheticEvent } from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import type { InternationalAlpha2, Iso3166Alpha3, Iso3166Location } from './locations'
import { getAlpha2From3, useLocations } from './locations'
import parsePhoneNumber from './parsePhoneNumber'
import { INTERNATIONAL_CODE, INTERNATIONAL_VALUE, LocationOptions } from './constants'
import defaultFirstLocations from './defaultFirstLocations'
import { internationalLabelString, locationLabelString } from './strings'
import type { PhoneAlpha2, PhoneAlpha3, PhoneNumberSyntheticEventFn } from './types'

const debug = zionDebug('zion:phone-number')

type LocationOption = PhoneAlpha2 | typeof LocationOptions.DIVIDER | typeof LocationOptions.INTERNATIONAL

const emptyArray = []

type InternalPhoneNumberPropsParams = {
  onlyLocations?: PhoneAlpha2[]
  firstLocationsBase?: PhoneAlpha2[]
  locations?: Iso3166Location[]
  locationLabel?: string
  defaultLocationAlpha2Or3?: PhoneAlpha2 | PhoneAlpha3 | InternationalAlpha2
  name?: string
  parentOnBlur?: PhoneNumberSyntheticEventFn
  parentOnChange?: PhoneNumberSyntheticEventFn
  rawValue?: string
}

type InternalPhoneNumberProps = {
  defaultLocation?: PhoneAlpha2
  labels: Partial<Record<PhoneAlpha2 | InternationalAlpha2 | 'country', string>>
  onBlur(event: SyntheticEvent): void
  onChange(value?: string): void
  sortedLocations: LocationOption[]
  sortedLocationsNoDivider: PhoneAlpha2[]
  value: string
}

export default function useInternalPhoneNumberProps({
  onlyLocations,
  firstLocationsBase,
  locations,
  locationLabel,
  defaultLocationAlpha2Or3,
  name,
  parentOnBlur,
  parentOnChange,
  rawValue,
}: InternalPhoneNumberPropsParams): InternalPhoneNumberProps {
  const supportedUserLocation = useSupportedUserLocation(onlyLocations)
  // have to provide full sorted list otherwise react-phone-number-input will sort
  const { labels, sortedLocationsNoDivider, sortedLocations } = useSortedLocationsAndLabels({
    firstLocationsBase,
    supportedUserLocation,
    onlyLocations,
    locations,
    locationLabel,
  })
  const defaultLocationAlpha2 = useDefaultLocationAlpha2(defaultLocationAlpha2Or3)
  const defaultLocation = useDefaultLocation({
    defaultLocationAlpha2,
    onlyLocations,
    supportedUserLocation,
  })
  const value = useE164Value(rawValue, defaultLocation)
  const onChange = useCallback(
    (changeString = '') => {
      if (!parentOnChange) {
        return
      }
      // Pass up an event object to work inside forms and to be consistent with TextField behavior
      parentOnChange({
        type: 'change',
        target: {
          value: changeString,
          name,
        },
      })
    },
    [name, parentOnChange]
  )
  const onBlur = useCallback(() => {
    if (!parentOnBlur) {
      return
    }
    // Pass value in the onBlur so that the value is E.164 instead of the raw inputValue
    parentOnBlur({
      type: 'blur',
      target: {
        value,
        name,
      },
    })
  }, [name, parentOnBlur, value])

  useEffect(() => {
    if (value && value !== rawValue) {
      debug('Calling onChange to reconcile internal value with externally provided value (non-E.164)')
      onChange(value)
    }
  }, [value, rawValue, onChange])

  return { defaultLocation, labels, onBlur, onChange, sortedLocations, sortedLocationsNoDivider, value }
}

function useSupportedUserLocation(onlyLocations: PhoneAlpha2[]): boolean {
  return useMemo(() => {
    const userLocation = geoData?.country
    return userLocation && onlyLocations.includes(userLocation)
  }, [onlyLocations])
}

function useSortedLocationsAndLabels({
  firstLocationsBase = defaultFirstLocations,
  supportedUserLocation,
  onlyLocations,
  locations,
  locationLabel,
}): { sortedLocations: LocationOption[]; labels: Record<string, string>; sortedLocationsNoDivider: PhoneAlpha2[] } {
  // cannot call hook conditionally so if client passed custom locations then just pass emptyArray which is effectively a no-op
  const useLocationsResult = useLocations({ onlyLocations: locations ? emptyArray : onlyLocations })
  const allLocations = locations || useLocationsResult
  return useMemo(() => {
    const allAlpha2s = allLocations.map(({ alpha2 }) => alpha2)
    // passing null for this prop is supported (so using default value for argument)
    const firstLocationsUnfiltered = firstLocationsBase || emptyArray
    if (supportedUserLocation && !firstLocationsUnfiltered.includes(supportedUserLocation)) {
      firstLocationsUnfiltered.push(supportedUserLocation)
    }
    // make sure firstLocations are a subset of onlyLocations and sort at the same time
    const firstLocations = firstLocationsUnfiltered
      .filter((alpha2) => onlyLocations.includes(alpha2))
      // always respect order from useLocations/locations
      .sort((a, b) => allAlpha2s.indexOf(a) - allAlpha2s.indexOf(b))
    // calculate list of locations
    const filteredAlpha2s = firstLocations.length
      ? allAlpha2s.filter((alpha2: PhoneAlpha2) => !firstLocations.includes(alpha2))
      : allAlpha2s
    const sortedLocationsNoDivider = [...firstLocations, ...filteredAlpha2s]
    const sortedLocations = firstLocations.length
      ? [LocationOptions.INTERNATIONAL, ...firstLocations, LocationOptions.DIVIDER, ...filteredAlpha2s]
      : [LocationOptions.INTERNATIONAL, ...sortedLocationsNoDivider]
    const labels = {
      country: locationLabel || locationLabelString(),
      // phone: 'Phone',
      // ext: 'ext.',
      [INTERNATIONAL_CODE]: internationalLabelString(),
    }
    allLocations.forEach(({ alpha2, text }) => {
      labels[alpha2] = text
    })
    return { sortedLocations, labels, sortedLocationsNoDivider }
  }, [allLocations, firstLocationsBase, onlyLocations, supportedUserLocation, locationLabel])
}

// convert to upper case alpha2
function useDefaultLocationAlpha2(defaultLocationAlpha2Or3: string): string | undefined {
  return useMemo(() => {
    if (!defaultLocationAlpha2Or3) {
      return null
    }
    const code = defaultLocationAlpha2Or3.toUpperCase()
    if (code.length === 2) {
      return code
    }
    return getAlpha2From3(code as Iso3166Alpha3)
  }, [defaultLocationAlpha2Or3])
}

function useDefaultLocation({ defaultLocationAlpha2, onlyLocations, supportedUserLocation }): PhoneAlpha2 {
  return useMemo(() => {
    // check if defaultLocationAlpha2 is supported
    const supported = defaultLocationAlpha2 && onlyLocations.includes(defaultLocationAlpha2)
    if (supported) {
      return defaultLocationAlpha2
    }
    // find reasonable default
    // if we want to allow more than what is in onlyLocations here we still need to check
    // that it's supported with the react-phone-number-input otherwise
    // that library code may blow up at certain points
    return supportedUserLocation || INTERNATIONAL_VALUE
  }, [defaultLocationAlpha2, onlyLocations, supportedUserLocation])
}

// ensure value passes to react-phone-number-input is E.164 value
// otherwise editing existing number will have the wrong location selected
function useE164Value(rawValue: string, defaultLocation: PhoneAlpha2): string {
  return useMemo(() => {
    const value = rawValue || ''
    if (!value.length) {
      return value
    }
    if (value.startsWith('+')) {
      return value
    }
    debug('E.164 value was not provided to PhoneNumber')
    const phoneNumberObj = parsePhoneNumber(value, defaultLocation)
    return phoneNumberObj.number || value
  }, [defaultLocation, rawValue])
}
